// Chips horizontales para filtrar pacientes en DashboardKine.
// Multi-select con lógica AND (intersección): si se selecciona "Baja
// adherencia" + "Dolor reportado", solo aparecen pacientes con AMBAS.
//
// Sprint 3 — feedback 2da IA: el dashboard del kine es la pantalla más
// importante. Triage rápido > listado plano.
//
// Counter por chip refleja cuántos pacientes matchearían si solo ese
// chip estuviera activo (no la intersección con otros chips). Esto es
// más útil UX: el kine ve cuántos pacientes hay en cada bucket
// independientemente, no condicionado a las otras selecciones.

import { AlertTriangle, TrendingDown, Activity, Sparkles, X } from "lucide-react";

export type FilterKey =
  | "needsReview"
  | "lowAdherence"
  | "hasPain"
  | "justJoined";

interface FilterDef {
  key: FilterKey;
  label: string;
  icon: typeof AlertTriangle;
  /** Tailwind classes para active state (filled). */
  activeClasses: string;
}

const FILTERS: readonly FilterDef[] = [
  {
    key: "needsReview",
    label: "Necesita revisión",
    icon: AlertTriangle,
    activeClasses: "bg-amber-500 text-white border-amber-500",
  },
  {
    key: "lowAdherence",
    label: "Baja adherencia",
    icon: TrendingDown,
    activeClasses: "bg-orange-500 text-white border-orange-500",
  },
  {
    key: "hasPain",
    label: "Dolor reportado",
    icon: Activity,
    activeClasses: "bg-red-500 text-white border-red-500",
  },
  {
    key: "justJoined",
    label: "Recién inscriptos",
    icon: Sparkles,
    activeClasses: "bg-blue-500 text-white border-blue-500",
  },
] as const;

interface Props {
  /** Set de filtros actualmente activos. */
  active: Set<FilterKey>;
  /** Toggle individual de un filtro. */
  onToggle: (key: FilterKey) => void;
  /** Limpia todos los filtros activos. */
  onClear: () => void;
  /** Counts por filtro (cuántos pacientes matchearían SOLO ese filtro,
   *  no la intersección). undefined mientras carga. */
  counts: Record<FilterKey, number> | undefined;
}

export function PatientFilterChips({
  active,
  onToggle,
  onClear,
  counts,
}: Props) {
  const hasActive = active.size > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {FILTERS.map((f) => {
        const isActive = active.has(f.key);
        const count = counts?.[f.key];
        const Icon = f.icon;
        return (
          <button
            key={f.key}
            type="button"
            onClick={() => onToggle(f.key)}
            aria-pressed={isActive}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ease-out ${
              isActive
                ? f.activeClasses + " shadow-sm"
                : "bg-card text-foreground border-border hover:border-primary/40 hover:bg-muted/50"
            }`}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={2} />
            <span>{f.label}</span>
            {count !== undefined && count > 0 && (
              <span
                className={`tabular-nums ${
                  isActive ? "opacity-90" : "text-muted-foreground"
                }`}
              >
                ({count})
              </span>
            )}
          </button>
        );
      })}
      {hasActive && (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
          Limpiar
        </button>
      )}
    </div>
  );
}
